import { CommonModule } from '@angular/common';
import { Component, OnInit, Type, ViewContainerRef, inject, viewChild } from '@angular/core';
import { Observable } from 'rxjs';
import { UrlFeatureFlagConfig, UrlFeatureFlagService } from './url-feature-flag.service';

// Generic interface that constrains services to have loadFeatureFlags method
export interface FeatureFlagService {
  loadFeatureFlags(): Observable<Record<string, boolean>>;
}

export interface FeatureFlagConfig {
  featureFlagName?: string;
  urlFeatureFlag?: UrlFeatureFlagConfig;
  featureFlagService?: Type<FeatureFlagService>;
}

export function componentSwitcher(
  config: FeatureFlagConfig | string,
  componentIfEnabled: Type<unknown>,
  componentIfDisabled: Type<unknown>,
): Type<any> {
  // Backward compatibility: if config is a string, treat it as featureFlagName
  const featureFlagConfig: FeatureFlagConfig =
    typeof config === 'string' ? { featureFlagName: config } : config;

  @Component({
    standalone: true,
    imports: [CommonModule],
    template: '<ng-container #container></ng-container>',
  })
  class FeatureFlagSwitcherComponent implements OnInit {
    private readonly _urlFeatureFlagService = inject(UrlFeatureFlagService);
    private readonly _featureFlagsService = featureFlagConfig.featureFlagService
      ? inject(featureFlagConfig.featureFlagService)
      : null;

    container = viewChild('container', { read: ViewContainerRef });

    ngOnInit(): void {
      // If we have both feature flag and URL feature flag, check both
      if (
        featureFlagConfig.featureFlagName &&
        featureFlagConfig.urlFeatureFlag &&
        this._featureFlagsService
      ) {
        this._featureFlagsService.loadFeatureFlags().subscribe((flags) => {
          const featureFlagEnabled = flags[featureFlagConfig.featureFlagName!];
          const urlFeatureFlagEnabled = this._urlFeatureFlagService.isUrlFeatureFlagEnabledSync(
            featureFlagConfig.urlFeatureFlag!,
          );

          // Show enabled component if either feature flag OR URL parameter is enabled
          const selectedComponent =
            featureFlagEnabled || urlFeatureFlagEnabled ? componentIfEnabled : componentIfDisabled;

          this.container().createComponent(selectedComponent);
        });
      }
      // If we only have feature flag
      else if (featureFlagConfig.featureFlagName && this._featureFlagsService) {
        this._featureFlagsService.loadFeatureFlags().subscribe((flags) => {
          const selectedComponent = flags[featureFlagConfig.featureFlagName!]
            ? componentIfEnabled
            : componentIfDisabled;
          this.container().createComponent(selectedComponent);
        });
      }
      // If we only have URL feature flag
      else if (featureFlagConfig.urlFeatureFlag) {
        this._urlFeatureFlagService
          .isUrlFeatureFlagEnabled(featureFlagConfig.urlFeatureFlag)
          .subscribe((enabled) => {
            const selectedComponent = enabled ? componentIfEnabled : componentIfDisabled;
            this.container().createComponent(selectedComponent);
          });
      }
      // Fallback to disabled component if no config provided
      else {
        this.container().createComponent(componentIfDisabled);
      }
    }
  }

  return FeatureFlagSwitcherComponent;
}
